import IVat from "./vat"
import IRawCaratech from "./caratech"
import ITerritoi from "./territoi"
import {
    ILocalizedString,
    Multilang,
    MultilangString,
} from "./multilang-string"
import {isDroopyGuid, getErrorMessage, getlangFromCache} from "../../utils"
import IFileInfos from "./file-infos"
import {
    FormFieldTypes,
    IFieldState,
} from "../../ui/product-form/_reducers/_utils/product-form-types"
import {
    IFields,
    IStandardFields,
} from "../../ui/product-form/_reducers/form-id-state"
import {FieldType, ITranslations, LockStatus} from ".."
import {ITreeNode} from "../../utils/tree/interfaces"
import {isEmpty} from "lodash"
import {stringify} from "../../utils/stringifyier"
import {ITax, ITechRawData} from "../bo"
import {product} from "ramda";
import {LocalStorageKeys} from "../../utils/local-storage-keys";

export interface IProductFieldProps {
    [paramKey: string]: IFieldState<any>
}

const generateFormFieldsFromValues = (productValues: IRawSupplierReference) => {
    return Object.keys(productValues).reduce((accumulator: any, key: any) => {
        return {
            ...accumulator,
            [key]: {
                code: key,
                localized: false,
                value: productValues[key],
                errorText: "",
                fieldType: FieldType.Standard,
                regenerateInfos: {fieldName: key},
                formFieldType: FormFieldTypes.Checkbox,
                isRequired: true,
                lock: "unlock",
            },
        }
    }, {})
}

export const convertReceivedProduct = (product: IRawSupplierReference) => {
    product.supplierRef =
        isDroopyGuid(product.supplierRef) && !product.isTopRef
            ? ""
            : product.supplierRef

    product.isValid =
        product.isTopRef ||
        (!!product.ean && !!product.supplierRef && !!product.manufacturerRef)

    return product
}

// -------------------------------------- USED BY PRODUCT-FORM

export const getProductFieldErrors = (
    product: IProductFieldProps,
    notRequiredList: string[],
    translations?: any
) => {
    const result = {}
    for (const key of Object.keys(product)) {
        if (notRequiredList.find((value) => value === key)) {
            product[key].isRequired = false
        }
        // @ts-ignore
        const errorMessage = getErrorMessage(product[key], translations || {})
        if (errorMessage !== "") {
            Object.assign(result, {[key]: errorMessage})
        }
    }
    return result
}

// ----------------------------- REFACTRO SECTION
export interface IReferenceIds {
    droopyId: string
    supplierRef: string
    topRef?: string
    topDroopyId?: string
    supplierName: string
    manefacturerRef: string
    isTopRef: boolean
}

export interface IRawSupplierReference {
    droopyId: string
    supplierRef: string
    isTopRef: boolean
    topRef?: string
    topDroopyId?: string
    supplierName: string
    label: MultilangString
    ean: string
    price: number
    comNode: string
    customsCode: string
    techNode: string
    manufacturerRef: string
    picture: IFileInfos | null
    activated: boolean
    validated: boolean
    validatedOnce: string
    minOrder: number
    originCountry: ITerritoi
    brand: string
    vatRate: IVat
    assemblyTime: string
    supplierWarranty: string
    bruneauWarranty: string | null
    isValid?: boolean
    deleted: boolean
    declined: IRawSupplierDeclinedReference[] | null
}

export const completeRawDeclined = (
    topRef: IRawSupplierReference,
    declined: IRawSupplierDeclinedReference
): IRawSupplierReference => ({
    ...topRef,
    ...declined,
    declined: null,
    isTopRef: false,
})

export interface IRawSupplierDeclinedReference {
    droopyId: string
    activated: boolean
    validated: boolean
    validatedOnce: string
    deleted: boolean
    supplierRef: string
    manufacturerRef: string
    supplierName: string
    price: number
    ean: string
    label: MultilangString
    isValid: boolean
    picture: IFileInfos | null
}

export interface ISupplierReference
    extends Omit<IRawSupplierReference, "picture" | "declined"> {
    purchaseLabel: MultilangString
    purchaseIntroduction: MultilangString
    buyingReasons: MultilangString
    pictures: IFileInfos[]
    pdf: IFileInfos[]
    techFields: IRawCaratech[]
    declined: ISupplierDeclinedReference[] | null
    selectedTaxes: string[]
    taxValues: ITax[]
    previousVersionDates?: string[]
}

export const completeDeclined = (
    topRef: ISupplierReference,
    declined: ISupplierDeclinedReference
): ISupplierReference => ({
    ...topRef,
    ...declined,
    declined: null,
    isTopRef: false,
})

export interface ISupplierDeclinedReference
    extends Omit<IRawSupplierDeclinedReference, "picture"> {
    topRef: string
    purchaseLabel: MultilangString
    buyingReasons: MultilangString
    pictures: IFileInfos[]
    techFields: IRawCaratech[]
    previousVersionDates?: string[]
}

export interface IOldValue {
    value:
        | ITerritoi
        | IVat
        | boolean
        | number
        | string
        | MultilangString
        | ILocalizedString[]
        | string[]
    isEmpty: boolean
    rawDate: string
    formattedDate: string
}

export const createOldValue = <
    T extends | ITerritoi
        | IVat
        | boolean
        | number
        | string
        | MultilangString
        | ILocalizedString[]
        | undefined
        | string[]
>(
    date: string | null,
    oldValue: T,
    previousVersionObject: ISupplierReference | undefined
): IOldValue | undefined =>
    !!previousVersionObject && !!date && !isDroopyGuid(oldValue + "")
        ? ({
            value: oldValue || "N/A",
            isEmpty: !oldValue,
            rawDate: date,
            formattedDate: new Date(date).toLocaleDateString([], {
                hour: "2-digit",
                minute: "2-digit",
            }),
        } as IOldValue)
        : undefined

export const getReferenceIds = (
    ref: ISupplierReference | IRawSupplierReference
): IReferenceIds => ({
    droopyId: ref.droopyId,
    manefacturerRef: ref.manufacturerRef,
    supplierName: ref.supplierName,
    supplierRef: ref.supplierRef,
    topRef: ref.topRef,
    topDroopyId: ref.topDroopyId,
    isTopRef: ref.isTopRef,
})

export const getReferenceIdsFromList = (
    references: ISupplierReference[]
): IReferenceIds[] => references.map(getReferenceIds)

export const getSupplierRefFromIds = (
    references: Array<ISupplierReference>,
    ids: IReferenceIds
) =>
    references.find(
        (ref) =>
            ref.droopyId === ids.droopyId &&
            ref.supplierName === ids.supplierName &&
            ref.supplierRef === ids.supplierRef &&
            ref.manufacturerRef === ids.manefacturerRef &&
            ref.isTopRef === ids.isTopRef
    )

// export const groupTopRefAndVariants = (
//   references: IRawSupplierReference[],
//   showDeletedRefs: boolean
// ) => {
//   const tmp = references
//     .filter((ref) => ref.isTopRef)
//     .map((ref): IRawSupplierTopRef | IRawSupplierReference => {
//       if (ref.isTopRef) {
//         return {
//           ...ref,
//           variants: references.filter((r) => r.topRefId === ref.supplierRef),
//         }
//       } else {
//         return ref
//       }
//     })

//   let groupedReferences = [
//     ...tmp,
//     ...references.filter((o) => !o.isTopRef && !o.topRefId),
//   ].sort((a, b) => (a.droopyId > b.droopyId ? 1 : -1))

//   if (showDeletedRefs) {
//     groupedReferences = groupedReferences
//       .filter(
//         (ref) =>
//           ref.deleted ||
//           (ref as IRawSupplierTopRef).variants?.some((o) => o.deleted)
//       )
//       .map((ref): IRawSupplierTopRef | IRawSupplierReference => {
//         if (ref.isTopRef) {
//           return {
//             ...ref,
//             variants: (ref as IRawSupplierTopRef).variants.filter(
//               (r) => r.deleted
//             ),
//           }
//         } else {
//           return ref
//         }
//       })
//   } else {
//     groupedReferences = groupedReferences
//       .filter((ref) => !ref.deleted)
//       .map((ref): IRawSupplierTopRef | IRawSupplierReference => {
//         if (ref.isTopRef) {
//           return {
//             ...ref,
//             variants: (ref as IRawSupplierTopRef).variants.filter(
//               (r) => !r.deleted
//             ),
//           }
//         } else {
//           return ref
//         }
//       })
//   }

//   return groupedReferences
// }

interface ICheckFieldsValidityProps {
    fields: IFields
    notRequiredList: string[]
    translations: ITranslations["fieldErrors"]
}

export const checkFieldsValidity = ({
                                        fields,
                                        notRequiredList,
                                        translations,
                                    }: ICheckFieldsValidityProps): { [fieldName: string]: string } => {
    return Object.values(fields).reduce((acc, f) => {
        const errorMessage = notRequiredList.includes(f.code)
            ? ""
            : getErrorMessage(f, translations)
        if (errorMessage !== "") {
            return {...acc, [f.code]: errorMessage}
        }
        return acc
    }, {})
}

interface ICheckFieldValidityProps {
    field: IFieldState<any>
    translations: ITranslations["fieldErrors"]
}

export const checkFieldValidity = ({
                                       field,
                                       translations,
                                   }: ICheckFieldValidityProps) => getErrorMessage(field, translations)

export default ISupplierReference

export const mergeSelectedRawProducts = (
    productList: IRawSupplierReference[]
): IRawSupplierReference => {
    const lang = getlangFromCache()
    const tmp = createEmptyRawProduct(lang)
    const mergedProduct = Object.keys(tmp).reduce(
        (acc: IRawSupplierReference, key: string) => {
            const value = productList[0][key]
            if (productList.every((o) => stringify(o[key]) === stringify(value))) {
                acc[key] = value
            }
            return acc
        },
        createEmptyRawProduct(lang)
    )

    return mergedProduct
}

const createEmptyRawProduct = (lang: string): IRawSupplierReference => ({
    droopyId: "",
    supplierRef: "",
    isTopRef: false,
    topRefId: "",
    supplierName: "",
    label: Multilang.String.CreateNew(),
    ean: "",
    price: 0,
    comNode: "",
    techNode: "",
    manefacturerRef: "",
    picture: null,
    activated: false,
    validated: false,
    minorder: 0,
    // @ts-ignore
    originCountry: [],
    // @ts-ignore
    vatRate: [],
    assemblyTime: "",
    supplierWarranty: "",
    bruneauWarranty: null,
    deleted: false,
    customsCode: "",
})

export const mergeSelectedFullProducts = (
    productLis: ISupplierReference[]
): ISupplierReference => {
    const lang = getlangFromCache()
    const tmp = createEmptyFullProduct(lang)
    const mergedProduct = Object.keys(tmp).reduce(
        (acc: ISupplierReference, key: string) => {
            const toMerge = productLis.reduce(
                (acc: ISupplierReference[], product: ISupplierReference) => {
                    acc = [
                        ...acc,
                        product,
                        ...(product.declined?.map((declined) =>
                            completeDeclined(product, declined)
                        ) || []),
                    ]
                    return acc
                },
                []
            )
            const value = toMerge[0][key]
            if (
                value &&
                toMerge.every((o) => stringify(o[key]) === stringify(value))
            ) {
                acc[key] = value
            } else if (key === "techFields") {
                toMerge[0][key].forEach((field: IRawCaratech) => {
                    if (
                        toMerge.every((o) =>
                            o.techFields.find((t) => stringify(t) === stringify(field))
                        )
                    ) {
                        acc.techFields = [...acc.techFields, field]
                    }
                })
            }
            return acc
        },
        createEmptyFullProduct(lang)
    )

    return mergedProduct
}

const createEmptyFullProduct = (lang: string): ISupplierReference => ({
    droopyId: "",
    supplierRef: "",
    isTopRef: false,
    topRefId: "",
    supplierName: "",
    label: Multilang.String.CreateNew(),
    purchaseLabel: Multilang.String.CreateNew(),
    purchaseIntroduction: Multilang.String.CreateNew(),
    buyingReasons: Multilang.String.CreateNew(),
    ean: "",
    price: 0,
    comNode: "",
    techNode: "",
    manefacturerRef: "",
    pictures: [],
    pdf: [],
    activated: false,
    validated: false,
    minOrder: 0,
    // @ts-ignore
    originCountry: [],
    // @ts-ignore
    vatRate: [],
    assemblyTime: "",
    supplierWarranty: "",
    bruneauWarranty: "-1",
    techFields: [],
    deleted: false,
    customsCode: "",
})

export const sanitizeProduct = (product: ISupplierReference, techFieldsRawData: ITechRawData[]) : ISupplierReference => ({
    ...product,
    techFields: product.techFields.map(x => sanitizeTechFields(x, techFieldsRawData)),
    declined: product.declined?.map(d => sanitizeDeclined(d, techFieldsRawData)) || null
})

const sanitizeDeclined = (d: ISupplierDeclinedReference, techFieldsRawData: ITechRawData[]) : ISupplierDeclinedReference => ({
    ...d,
    techFields: d.techFields.map(x => sanitizeTechFields(x, techFieldsRawData))
})

const sanitizeTechFields = (field: IRawCaratech, techFieldsRawData: ITechRawData[]) => {
    let sanitizedField = field
    const raw = techFieldsRawData.find((t) => t.codeEnum === field.codeEnum)
    if (raw && raw.isDeclinable && Multilang.String.IsMultilangString(field.value))
        sanitizedField = {
            ...field,
            value: Multilang.String.DeleteFrom(field.value as MultilangString, localStorage[LocalStorageKeys.LANG] || "")
        }
    return sanitizedField
}

interface IFormatGetPropductResponse {
    product: ISupplierReference
    lock?: LockStatus
    comRawData?: ITreeNode[]
    techRawData?: ITreeNode[]
}

export const formatGetProductResponse = ({
                                             product,
                                             lock = "unlock",
                                             comRawData,
                                             techRawData,
                                         }: IFormatGetPropductResponse): ISupplierReference => {
    const getTreeState = (rawDatas: any[], node: any) => {
        let toggled = [node.id]
        if (node.fatherId !== "") {
            const nextNode = rawDatas.find((n) => n.id + "" === node.fatherId + "")
            toggled = [...toggled, ...getTreeState(rawDatas, nextNode)]
        }
        return toggled
    }

    const values = Object.keys(product).reduce(
        (accumulator: any, key: string) => {
            let value
            switch (key) {
                case "comNode":
                    if (!comRawData) {
                        return accumulator
                    }
                    value = comRawData.find((data) => data.code === product[key])

                    if (!isEmpty(value)) {
                        accumulator.comTreeState = {
                            checked: [value?.value],
                            expanded: getTreeState(comRawData, value),
                        }
                    }
                    break

                case "techNode":
                    if (!techRawData) {
                        return accumulator
                    }
                    value = techRawData.find((data) => data.code === product[key])

                    if (!isEmpty(value)) {
                        accumulator.techTreeState = {
                            checked: [value?.value],
                            expanded: getTreeState(techRawData, value),
                        }
                    }
                    break

                case "pictures":
                case "pdf":
                    value = product[key].map((infos: any, index: number) => ({
                        path: infos.path,
                        blob: infos.path,
                        file: {name: infos.name},
                        isMain: index === 0,
                        name: infos.name,
                        isNew: false,
                    }))
                    break
                case "supplierRef":
                    value =
                        isDroopyGuid(product[key]) && !product.isTopRef ? "" : product[key]
                    break
                default:
                    value = product[key]
                    break
            }

            if (value !== undefined) {
                accumulator[key] = value
            }
            return accumulator
        },
        {}
    )

    return values
}
